import { useMemo, useState } from "react";
import { Flame, Phone, MessageCircle, Search, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";
import AttemptCounter from "@/components/AttemptCounter";
import ContactPolicyBadge from "@/components/ContactPolicyBadge";
import { api } from "@/lib/api";

const logAttempt = (leadId, channel, source) => {
  // Fire-and-forget — counter UI refreshes on next list reload
  api
    .post(`/admin/leads/${leadId}/attempts`, { channel, source })
    .catch(() => {
      /* silent */
    });
};

const TEMP_DOT = {
  Hot: "bg-rose-500",
  Warm: "bg-amber-500",
  Cool: "bg-sky-500",
  Cold: "bg-slate-400",
};

const STATUS_PILL = {
  New: "bg-blue-100 text-blue-800",
  Contacted: "bg-amber-100 text-amber-800",
  "Follow-up": "bg-indigo-100 text-indigo-800",
  "Zoom Done": "bg-violet-100 text-violet-800",
  Visited: "bg-emerald-100 text-emerald-800",
  "Docs Recd": "bg-teal-100 text-teal-800",
  Enrolled: "bg-green-100 text-green-800",
  "Closed-lost": "bg-slate-100 text-slate-400",
  "Wrong number": "bg-rose-100 text-rose-700",
  // Legacy
  Closed: "bg-green-100 text-green-800",
};

const TABS = [
  { v: "mine", l: "Mine" },
  { v: "hot", l: "Hot" },
  { v: "all", l: "All" },
];

function waUrl(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return null;
  const withCc = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCc}?text=${encodeURIComponent(
    "Hi! Reaching out about your MBBS scholarship test. Free to talk?",
  )}`;
}

/**
 * MobileLeadsList — card-based responsive list shown to counsellors on phones.
 * Hidden on `md+` screens (desktop table takes over).
 */
export default function MobileLeadsList({ leads, userId, onSelect }) {
  const [tab, setTab] = useState("mine");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    let rows = Array.isArray(leads) ? leads : [];
    if (tab === "mine") rows = rows.filter((l) => l.assigned_to === userId);
    if (tab === "hot") rows = rows.filter((l) => l.lead_temperature === "Hot");
    if (q.trim()) {
      const s = q.toLowerCase();
      rows = rows.filter(
        (l) =>
          (l.name || "").toLowerCase().includes(s) ||
          (l.phone || "").toLowerCase().includes(s) ||
          (l.email || "").toLowerCase().includes(s),
      );
    }
    // Stable order: by score desc within tab when "hot", else by created desc
    if (tab === "hot") {
      rows = [...rows].sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0));
    }
    return rows;
  }, [leads, tab, q, userId]);

  const counts = useMemo(() => {
    const safe = Array.isArray(leads) ? leads : [];
    const mine = safe.filter((l) => l.assigned_to === userId).length;
    const hot = safe.filter((l) => l.lead_temperature === "Hot").length;
    return { mine, hot, all: safe.length };
  }, [leads, userId]);

  return (
    <div className="md:hidden" data-testid="mobile-leads-list">
      {/* Tabs */}
      <div className="sticky top-16 z-10 -mx-4 mb-3 border-b border-slate-200 bg-white px-4">
        <div className="flex gap-1 pt-2">
          {TABS.map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => setTab(t.v)}
              data-testid={`mobile-tab-${t.v}`}
              className={`relative -mb-px flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                tab === t.v
                  ? "border-[#0A2540] text-[#0A2540]"
                  : "border-transparent text-slate-500 hover:text-[#0A2540]"
              }`}
            >
              {t.l}
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                  tab === t.v ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                {t.v === "mine" ? counts.mine : t.v === "hot" ? counts.hot : counts.all}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="mb-3 relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, phone, email…"
          className="h-10 pl-9"
          data-testid="mobile-search"
        />
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          {tab === "mine"
            ? "No leads assigned to you yet. Claim a lead from the All tab."
            : tab === "hot"
              ? "No hot leads right now."
              : "No leads found."}
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.slice(0, 100).map((l) => (
            <li
              key={l.id}
              data-testid={`mobile-lead-${l.id}`}
              className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow active:shadow-inner"
            >
              <button
                type="button"
                className="flex w-full items-start gap-3 text-left"
                onClick={() => onSelect?.(l)}
              >
                <div
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                    TEMP_DOT[l.lead_temperature] || "bg-slate-300"
                  }`}
                  aria-label={l.lead_temperature || "—"}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-[#0A2540]">
                      {l.name || "—"}
                    </span>
                    {l.lead_temperature === "Hot" && (
                      <Flame className="h-3 w-3 text-rose-500" />
                    )}
                    {typeof l.lead_score === "number" && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {l.lead_score}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-slate-500">
                    {l.phone || "—"} · {l.country_preference || l.country || "—"}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        STATUS_PILL[l.status] || "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {l.status || "New"}
                    </span>
                    {l.scholarship_tier && l.scholarship_tier !== "None" && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                        {l.scholarship_tier}
                      </span>
                    )}
                    {l.attempt_status && (
                      <AttemptCounter status={l.attempt_status} variant="compact" />
                    )}
                    <ContactPolicyBadge lead={l} compact />
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
              </button>

              {/* One-tap actions */}
              <div className="mt-2.5 flex items-center gap-2 border-t border-slate-100 pt-2.5">
                {l.phone && (
                  <a
                    href={`tel:${String(l.phone).replace(/\D/g, "")}`}
                    data-testid={`mobile-call-${l.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      logAttempt(l.id, "call", "mobile_card");
                    }}
                  >
                    <Phone className="h-3.5 w-3.5" />
                    Call
                  </a>
                )}
                {l.phone && (
                  <a
                    href={waUrl(l.phone)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`mobile-wa-${l.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-[#25D366]/10 px-3 py-2 text-xs font-medium text-[#128C7E] hover:bg-[#25D366]/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      logAttempt(l.id, "whatsapp", "mobile_card");
                    }}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    WhatsApp
                  </a>
                )}
                <div
                  className="shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`mobile-voice-${l.id}`}
                >
                  <VoiceNoteRecorder leadId={l.id} compact />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {filtered.length > 100 && (
        <div className="mt-3 text-center text-[11px] text-slate-500">
          Showing first 100 of {filtered.length} leads
        </div>
      )}
    </div>
  );
}
